import { useEffect } from 'react';

import { differenceInMilliseconds } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';

import useLogin from 'src/hooks/useLogin';
import { remoteConfigAtom } from 'src/stores/auth/atoms';
import { getUpdatedUserInfoAtom, lastUserInfoUpdateDateAtom } from 'src/stores/user/atom';

const DEFAULT_REFRESH_USER_INFO_MIN_INTERVAL_SEC = 10; // 10초

const DEFAULT_REFRESH_USER_INFO_INTERVAL_SEC = 14400; // 4시간

/**
 * 웹은 토큰 방식으로 인해 로그인 주기가 길기 때문에 주기적으로 유저 정보를 최신화하는 훅
 */
export default function useUpdateUserInfo() {
  const remoteConfig = useAtomValue(remoteConfigAtom);
  const getUpdatedUserInfo = useSetAtom(getUpdatedUserInfoAtom);
  const lastUserInfoUpdateDate = useAtomValue(lastUserInfoUpdateDateAtom);
  const { isLogin } = useLogin();

  useEffect(() => {
    if (!isLogin) return;

    if (
      /**
       * 새로고침했을 때 가장 최근에 유저 정보를 갱신한 시점에서 일정 시간 이내라면 최신화된 상태로 간주하고 미갱신
       */
      lastUserInfoUpdateDate &&
      differenceInMilliseconds(new Date(), new Date(lastUserInfoUpdateDate)) <
        (remoteConfig?.refreshUserInfoMinIntervalInSeconds ??
          DEFAULT_REFRESH_USER_INFO_MIN_INTERVAL_SEC) *
          1000
    )
      return;

    getUpdatedUserInfo();
  }, [
    lastUserInfoUpdateDate,
    getUpdatedUserInfo,
    remoteConfig?.refreshUserInfoMinIntervalInSeconds,
    isLogin,
  ]);

  useEffect(() => {
    if (!isLogin) return;
    const timer = setInterval(
      getUpdatedUserInfo,
      (remoteConfig?.refreshUserInfoIntervalInSeconds ?? DEFAULT_REFRESH_USER_INFO_INTERVAL_SEC) *
        1000
    );
    return () => {
      clearInterval(timer);
    };
  }, [getUpdatedUserInfo, remoteConfig?.refreshUserInfoIntervalInSeconds, isLogin]);
}
